/**
 * Enterprise Register Form
 * Форма регистрации с валидацией и обработкой ошибок
 */

import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation, Link as RouterLink } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { logger } from "../../utils/logger";
import { isSafeInternalPath } from "../../utils/return-url";
import { CURRENT_TERMS_OF_SERVICE_VERSION } from "../../config/legal";
import { useTranslation } from "react-i18next";
import { fallbackLng } from "../../i18n/config";
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
  Grid,
  FormControlLabel,
  Checkbox,
  FormHelperText,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  Email,
  Lock,
  Person,
  CardGiftcard,
} from "@mui/icons-material";
import { createRegisterSchema, extractZodErrors } from "../../utils/validators";

/**
 * Достаёт путь возврата из location.state.from без доверия к форме (state мог
 * выставить любой navigate): принимается только внутренний путь приложения.
 * Пишет LoginForm (пробрасывает state.from от ProtectedRoute по ссылке
 * «Зарегистрироваться»), потребляет RegistrationSuccessPage («Продолжить»).
 */
function extractFromPath(state: unknown): string | null {
  if (typeof state !== "object" || state === null || !("from" in state)) {
    return null;
  }
  const from = (state as { from: unknown }).from;
  if (typeof from !== "object" || from === null || !("pathname" in from)) {
    return null;
  }
  const pathname = (from as { pathname: unknown }).pathname;
  return typeof pathname === "string" && isSafeInternalPath(pathname)
    ? pathname
    : null;
}

/**
 * Реферальный код из ссылки приглашения `/register?ref=<код>` (AD-305). Код
 * только подставляется в поле: человек видит его и может исправить, а длину
 * проверяет схема формы.
 */
function extractReferralCode(search: string): string {
  return new URLSearchParams(search).get("ref")?.trim() ?? "";
}

export default function RegisterForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const { register, isAuthenticated, isLoading, error, clearError } = useAuth();
  const { t, i18n } = useTranslation("common");
  const fromPath = extractFromPath(location.state);

  const [formData, setFormData] = useState(() => ({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
    referralCode: extractReferralCode(location.search),
  }));
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsError, setTermsError] = useState("");

  /**
   * true — регистрация выполнена ИМЕННО в этой сессии формы: навигацию на
   * страницу «проверьте почту» делает handleSubmit, а не effect ниже.
   * Ставится ДО await register(): setState внутри register() может отрендерить
   * isAuthenticated=true (и запустить effect) раньше, чем продолжится handleSubmit.
   */
  const justRegisteredRef = useRef(false);

  // Уже-аутентифицированный пользователь зашёл на /register → на дашборд.
  // После свежей регистрации НЕ срабатывает (см. justRegisteredRef).
  useEffect(() => {
    if (isAuthenticated && !justRegisteredRef.current) {
      logger.debug(
        "RegisterForm: Пользователь уже аутентифицирован, перенаправляем",
      );
      navigate("/dashboard", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Очистка ошибок при размонтировании
  useEffect(() => {
    return () => {
      if (error) {
        clearError();
      }
    };
  }, [error, clearError]);

  const validateForm = (): boolean => {
    // См. LoginForm: схема собирается на месте, потому что её сообщения — строки,
    // вычисленные в момент сборки (AD-44).
    const result = createRegisterSchema(t).safeParse(formData);
    if (!result.success) {
      setFormErrors(extractZodErrors(result.error));
      return false;
    }
    setFormErrors({});
    return true;
  };

  /**
   * Обработка отправки формы
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!termsAccepted) {
      setTermsError(t("auth.legalAcceptRequired"));
      return;
    }

    if (!validateForm()) {
      return;
    }

    const email = formData["email"].trim().toLowerCase();

    try {
      logger.debug("RegisterForm: Отправляем форму регистрации");
      clearError();
      justRegisteredRef.current = true;

      await register({
        firstName: formData["firstName"].trim(),
        lastName: formData["lastName"].trim(),
        email,
        password: formData["password"],
        // termsAccepted проверен выше (строка ~91) — фиксируем принятую версию ToS (GDPR Art. 7)
        acceptedTermsVersion: CURRENT_TERMS_OF_SERVICE_VERSION,
        // AD-169: сохраняем язык, который человек ВИДЕЛ на этой форме. Без него сервер
        // запишет английский по умолчанию, и письма пойдут не на языке интерфейса.
        // `split` — потому что i18next отдаёт и региональные коды вида `en-US`.
        language: i18n.language.split("-")[0] ?? fallbackLng,
        ...(formData["referralCode"].trim() && {
          referralCode: formData["referralCode"].trim(),
        }),
      });

      logger.debug(
        "RegisterForm: Регистрация успешна, ведём на страницу «проверьте почту»",
      );
      navigate("/auth/registration-success", {
        replace: true,
        state: fromPath ? { email, from: fromPath } : { email },
      });
    } catch (error) {
      // Регистрация не удалась → возвращаем effect-редиректу штатное поведение
      justRegisteredRef.current = false;
      logger.error("RegisterForm: Ошибка регистрации", error);
      // Ошибка уже обработана в AuthContext
    }
  };

  /**
   * Обработка изменения полей формы
   */
  const handleInputChange =
    (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({
        ...prev,
        [field]: e.target.value,
      }));

      // Очищаем ошибку поля при изменении
      if (formErrors[field]) {
        setFormErrors((prev) => ({
          ...prev,
          [field]: "",
        }));
      }
    };

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Typography component="h1" variant="h4" gutterBottom>
            {t("auth.registerTitle")}
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            align="center"
            sx={{ mb: 3 }}
          >
            {t("auth.registerSubtitle")}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ width: "100%", mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ mt: 1, width: "100%" }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  id="firstName"
                  label={t("auth.firstName")}
                  name="firstName"
                  autoComplete="given-name"
                  autoFocus
                  value={formData["firstName"]}
                  onChange={handleInputChange("firstName")}
                  error={!!formErrors["firstName"]}
                  helperText={formErrors["firstName"]}
                  disabled={isLoading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Person />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  id="lastName"
                  label={t("auth.lastName")}
                  name="lastName"
                  autoComplete="family-name"
                  value={formData["lastName"]}
                  onChange={handleInputChange("lastName")}
                  error={!!formErrors["lastName"]}
                  helperText={formErrors["lastName"]}
                  disabled={isLoading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Person />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>

            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label={t("auth.email")}
              name="email"
              autoComplete="email"
              value={formData["email"]}
              onChange={handleInputChange("email")}
              error={!!formErrors["email"]}
              helperText={formErrors["email"]}
              disabled={isLoading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label={t("auth.password")}
              type={showPassword ? "text" : "password"}
              id="password"
              autoComplete="new-password"
              value={formData["password"]}
              onChange={handleInputChange("password")}
              error={!!formErrors["password"]}
              helperText={formErrors["password"]}
              disabled={isLoading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      disabled={isLoading}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              margin="normal"
              required
              fullWidth
              name="confirmPassword"
              label={t("auth.confirmPassword")}
              type={showConfirmPassword ? "text" : "password"}
              id="confirmPassword"
              autoComplete="new-password"
              value={formData["confirmPassword"]}
              onChange={handleInputChange("confirmPassword")}
              error={!!formErrors["confirmPassword"]}
              helperText={formErrors["confirmPassword"]}
              disabled={isLoading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle confirm password visibility"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      edge="end"
                      disabled={isLoading}
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              margin="normal"
              fullWidth
              name="referralCode"
              label={t("auth.referralCodeOptional")}
              id="referralCode"
              autoComplete="off"
              value={formData["referralCode"]}
              onChange={handleInputChange("referralCode")}
              error={!!formErrors["referralCode"]}
              helperText={formErrors["referralCode"]}
              disabled={isLoading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CardGiftcard />
                  </InputAdornment>
                ),
              }}
            />

            {/* Terms of Service acceptance */}
            <Box sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={termsAccepted}
                    onChange={(e) => {
                      setTermsAccepted(e.target.checked);
                      if (e.target.checked) setTermsError("");
                    }}
                    disabled={isLoading}
                    color="primary"
                    size="small"
                  />
                }
                label={
                  <Typography variant="body2" color="text.secondary">
                    {t("auth.legalAcceptPrefix")}{" "}
                    <RouterLink
                      to="/terms-of-service"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "inherit", fontWeight: 600 }}
                    >
                      {t("auth.legalAcceptTos")}
                    </RouterLink>{" "}
                    {t("auth.legalAcceptAnd")}{" "}
                    <RouterLink
                      to="/privacy-policy"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "inherit", fontWeight: 600 }}
                    >
                      {t("auth.legalAcceptPrivacy")}
                    </RouterLink>
                  </Typography>
                }
              />
              {termsError && (
                <FormHelperText error sx={{ ml: 4 }}>
                  {termsError}
                </FormHelperText>
              )}
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 2, mb: 2 }}
              disabled={isLoading || !termsAccepted}
              startIcon={isLoading ? <CircularProgress size={20} /> : null}
            >
              {isLoading ? t("auth.registering") : t("auth.register")}
            </Button>

            <Box sx={{ textAlign: "center", mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {t("auth.alreadyHaveAccount")}{" "}
                <RouterLink to="/auth/login" style={{ textDecoration: "none" }}>
                  <Typography component="span" variant="body2" color="primary">
                    {t("auth.signIn")}
                  </Typography>
                </RouterLink>
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
}
